import { Building } from '../../../../types/protocols';
import { BaseComponentImpl } from '../../../../types/implementations';
import { BuildComponentTypeDefinition } from '../../../../types/Enums';
import { wellFormedComponents } from '../../../../Helpers/componentIntegrity';
import { isConditionSet } from '../../../../Helpers/conditionUtils';

/**
 * Validation result for a single component
 */
export interface ComponentValidationResult {
    hasCondition: boolean;
    hasSpecificType: boolean;
    hasValidType: boolean;
}

/**
 * Component with validation status
 */
export interface InvalidComponent {
    component: BaseComponentImpl;
    validation: ComponentValidationResult;
}

/**
 * Validates a single component for condition and specificType
 * Type validation (checking if type exists in config) only happens if buildComponentTypes is provided
 * This is only done in ComponentValidation page (validate-maintenance-components)
 */
export function validateComponent(
    component: BaseComponentImpl,
    buildComponentTypes?: Record<string, BuildComponentTypeDefinition>
): ComponentValidationResult {
    const hasCondition = isConditionSet(component.condition?.value?.value);

    // Check if specificType exists
    const specificTypeValue = component.specificType?.value;
    let hasSpecificType = !!specificTypeValue;
    let hasValidType = true;

    // Type validation only happens if buildComponentTypes is provided (e.g., in ComponentValidation)
    if (buildComponentTypes && specificTypeValue) {
        hasValidType = buildComponentTypes[specificTypeValue] !== undefined;
        // If type is invalid, treat it as if no type was selected (only in ComponentValidation)
        if (!hasValidType) {
            hasSpecificType = false;
        }
    }
    
    return {
        hasCondition,
        hasSpecificType,
        hasValidType
    };
}

/**
 * Gets all components that are missing either condition or specificType, or have an invalid type
 */
export function getInvalidComponents(
    building: Building | null,
    buildComponentTypes?: Record<string, BuildComponentTypeDefinition>
): InvalidComponent[] {
    if (!building?.model?.structure?.components) {
        return [];
    }

    const invalidComponents: InvalidComponent[] = [];
    
    wellFormedComponents(building.model.structure.components).forEach(componentData => {
        const component = new BaseComponentImpl(componentData);
        const validation = validateComponent(component, buildComponentTypes);
        
        // Include if missing condition or missing/invalid type
        // Note: invalid types are treated as missing types in validation
        if (!validation.hasCondition || !validation.hasSpecificType) {
            invalidComponents.push({
                component,
                validation
            });
        }
    });

    return invalidComponents;
}

/**
 * Checks if all components in a building have both condition and specificType set
 * Type validation only happens if buildComponentTypes is provided (e.g., in ComponentValidation)
 * When type validation is enabled, invalid types are treated as missing types
 */
export function hasAllComponentsValid(
    building: Building | null,
    buildComponentTypes?: Record<string, BuildComponentTypeDefinition>
): boolean {
    if (!building?.model?.structure?.components) {
        return true; // No components = valid by default
    }

    const components = wellFormedComponents(building.model.structure.components);
    
    return components.every(componentData => {
        const component = new BaseComponentImpl(componentData);
        const validation = validateComponent(component, buildComponentTypes);
        // hasSpecificType will be false if type is invalid (when buildComponentTypes is provided)
        return validation.hasCondition && validation.hasSpecificType;
    });
}

/**
 * Counts how many components are missing each field
 */
export function getValidationStats(
    building: Building | null,
    buildComponentTypes?: Record<string, BuildComponentTypeDefinition>
): {
    total: number;
    missingCondition: number;
    missingSpecificType: number;
    missingBoth: number;
    invalidType: number;
    valid: number;
} {
    if (!building?.model?.structure?.components) {
        return {
            total: 0,
            missingCondition: 0,
            missingSpecificType: 0,
            missingBoth: 0,
            invalidType: 0,
            valid: 0
        };
    }

    const components = wellFormedComponents(building.model.structure.components);
    let missingCondition = 0;
    let missingSpecificType = 0;
    let missingBoth = 0;
    let invalidType = 0;
    let valid = 0;

    components.forEach(componentData => {
        const component = new BaseComponentImpl(componentData);
        const validation = validateComponent(component, buildComponentTypes);
        
        if (!validation.hasCondition && !validation.hasSpecificType) {
            missingBoth++;
        } else if (!validation.hasCondition) {
            missingCondition++;
        } else if (!validation.hasSpecificType) {
            // This includes both missing types and invalid types
            missingSpecificType++;
        } else {
            valid++;
        }
    });

    return {
        total: components.length,
        missingCondition,
        missingSpecificType,
        missingBoth,
        invalidType: 0, // Invalid types are now counted as missing types
        valid
    };
}
